import * as amplitude from "@amplitude/analytics-browser";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Slider,
  Stack,
  Switch,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useCallback, useContext, useState } from "react";

import AvatarWithStatus from "@/components/AvatarWithStatus";
import ElectronAppVersion from "@/components/electron/ElectronAppVersion.tsx";
import ElectronComponentLoader from "@/components/electron/ElectronComponentLoader.tsx";
import Loading from "@/components/Loading/Index";
import UserFeedbackForm from "@/components/UserFeedbackForm.tsx";
import DebugFlagsPanel from "@/components/Workspace/DebugFlagsPanel";
import { debugFlagRows } from "@/components/Workspace/debugFlagRows";
import WorkspaceDropdown from "@/components/WorkspaceDropdown.tsx";
import { ampli, logout } from "@/config";
import ModalForm from "@/elements/ModalForm";
import UseFirebase from "@/hooks/firebase/useFirebase";
import UseStorage from "@/hooks/useStorage";
import { AppContext } from "@/models/AppStateProvider";
import { enqueueCalculateUnreads, setUnreadAlsoSetAsReadOnNextBootstrap } from "@/models/actions/calculateUnreads";
import { forceReload } from "@/models/BootstrapUtils.ts";
import { previewNewMessageSound } from "@/models/newMessageSound";
import {
  MAX_NEW_MESSAGE_SOUND_VOLUME,
  MIN_NEW_MESSAGE_SOUND_VOLUME,
  NEW_MESSAGE_SOUND_IDS,
  NEW_MESSAGE_SOUND_VOLUME_STEP,
  type NewMessageSoundId,
  type NewMessageSoundScope,
} from "@/models/newMessageSoundRules";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { useWebSettings } from "@/models/StateProviders/WebSettingsProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { UxContext } from "@/models/UxStateProvider";
import PortalDialog from "@/ui/PortalDialog.tsx";
import { ipcRendererSend, isElectron } from "@/utils";

const NEW_MESSAGE_SOUND_HEADING_ID = "new-message-sound-scope-heading";
const NEW_MESSAGE_SOUND_ID_LABEL_ID = "new-message-sound-id-label";
const NEW_MESSAGE_SOUND_VOLUME_LABEL_ID = "new-message-sound-volume-label";

/** Space between sections, and padding inside each, so every block on the page lines up. */
const SECTION_GAP = 4;
const SECTION_PADDING_X = 2;

/** The app theme stretches every `Button` to full width; page actions size to their label instead. */
const COMPACT_BUTTON = { width: "auto", alignSelf: "flex-start", px: 3 };

/** One titled block of the profile page. */
function ProfileSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box component="section" sx={{ px: SECTION_PADDING_X }}>
      <Typography
        component="h2"
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "neutral.main",
          mb: 1.5,
        }}
      >
        {title}
      </Typography>
      <Stack sx={{ gap: 2 }}>{children}</Stack>
    </Box>
  );
}

/** The three ding scopes, in increasing breadth, with the copy that explains each one. */
const NEW_MESSAGE_SOUND_SCOPE_OPTIONS: { value: NewMessageSoundScope; label: string; helperText: string }[] = [
  { value: "off", label: "Off", helperText: "Never play a sound." },
  { value: "myDrivers", label: "My Drivers", helperText: "Play a sound only for drivers on your My Drivers list." },
  {
    value: "allDrivers",
    label: "All Drivers",
    helperText: "Play a sound for every driver you can see under All Drivers.",
  },
];

/** The label each sound is listed under. */
const NEW_MESSAGE_SOUND_LABELS: Record<NewMessageSoundId, string> = {
  beep: "Beep",
  pedalSteel: "Pedal steel",
  drum: "Drum",
};

/** A volume percent as the slider shows and announces it. */
function formatVolume(volume: number): string {
  return `${volume}%`;
}

export default function WorkspaceUserProfile() {
  const { flags, setFlags, launchDarklyFlags, overriddenFlagKeys, clearFlagOverride, clearFlagOverrides } =
    React.useContext(AppContext);
  const [debugFilter, setDebugFilter] = useState("");
  const { myAccount } = React.useContext(MyAccountContext);
  const { setUserFeedbackModalOpen, userFeedbackModalOpen } = React.useContext(UxContext);
  const { myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const [loggingOut, setLoggingOut] = React.useState<boolean>(false);
  const [reloading, setReloading] = React.useState<boolean>(false);
  const { showDebugMenu, allowLocalUserSettings } = flags;
  const { deleteActiveToken } = UseFirebase();
  const { clearLocalStorage } = UseStorage();
  const theme = useTheme();
  const { hideDriverMessages, setHideDriverMessages, flushWebSettings } = useContext(UxContext);
  const { settings, updateWebSettings } = useWebSettings();
  const [teamDispatchConfirmOpen, setTeamDispatchConfirmOpen] = useState<boolean>(false);
  const [clearingDb, setClearingDb] = useState(false);

  /**
   * Dual-writes the legacy `playSoundForNewMessages` boolean alongside the scope, so a user who chooses
   * here is not silenced on `web/release-259`, which reads only that boolean.
   */
  const handleNewMessageSoundScopeChange = (scope: NewMessageSoundScope) => {
    updateWebSettings({ newMessageSoundScope: scope, playSoundForNewMessages: scope !== "off" });
  };

  /** Saves the chosen sound and plays it once at the current volume, so the member hears what they picked. */
  const handleNewMessageSoundIdChange = (soundId: NewMessageSoundId) => {
    updateWebSettings({ newMessageSoundId: soundId });
    previewNewMessageSound(soundId, settings.newMessageSoundVolume);
  };

  /** Saves each step of a drag; the provider debounces the PATCH. The slider has one thumb, so `volume` is a number. */
  const handleNewMessageSoundVolumeChange = (volume: number | number[]) => {
    if (typeof volume === "number") updateWebSettings({ newMessageSoundVolume: volume });
  };

  /** Plays the chosen sound once at the volume the member let go at, rather than at every step of the drag. */
  const handleNewMessageSoundVolumeCommit = (volume: number | number[]) => {
    if (typeof volume === "number") previewNewMessageSound(settings.newMessageSoundId, volume);
  };

  const recordTeamDispatcherToggle = (enabling: boolean) => {
    const identifyEvent: amplitude.Identify = new amplitude.Identify();
    if (enabling) {
      ampli.toggleTeamDispatcherModeOn();
      identifyEvent.set("teamDispatcherMode", "on");
    } else {
      ampli.toggleTeamDispatcherModeOff();
      identifyEvent.set("teamDispatcherMode", "off");
    }
    amplitude.identify(identifyEvent);
  };

  const enableTeamDispatcherMode = async () => {
    recordTeamDispatcherToggle(true);
    setHideDriverMessages(true);
    // dump db and do a full-bootstrap
    // await dbtruncate and bootstrap
    setClearingDb(true);
    if (myCurrentWorkspaceMembership) {
      setUnreadAlsoSetAsReadOnNextBootstrap();
    }
    // Persist the toggle before the hard reload, otherwise the debounced PATCH is dropped and the
    // setting reads OFF once /userinfo is re-fetched on the next boot.
    await flushWebSettings?.();
    void forceReload();
  };

  const disableTeamDispatcherMode = () => {
    recordTeamDispatcherToggle(false);
    setHideDriverMessages(false);
    if (myCurrentWorkspaceMembership) {
      enqueueCalculateUnreads({
        membership: myCurrentWorkspaceMembership,
        alsoSetAsRead: false,
      });
    }
  };

  const handleTeamDispatcherToggleClick = () => {
    if (hideDriverMessages) {
      disableTeamDispatcherMode();
    } else {
      setTeamDispatchConfirmOpen(true);
    }
  };

  const handleTeamDispatchConfirm = () => {
    setTeamDispatchConfirmOpen(false);
    void enableTeamDispatcherMode();
  };

  const handleTeamDispatchCancel = () => {
    setTeamDispatchConfirmOpen(false);
  };

  const role = myCurrentWorkspaceMembership?.role;
  const showLocalUserSettingsMenu = allowLocalUserSettings && role === "member";

  const handleClearCacheAndReload = useCallback(() => {
    setReloading(true);
    forceReload()
      .then(() => window.location.reload())
      .catch(() => window.location.reload());
  }, []);

  const handleSubmitFeedback = () => {
    setUserFeedbackModalOpen(true);
  };

  const { forceLogout, setForceLogout } = useContext(UxContext);

  const handleLogout = async () => {
    ampli.logoutManually();
    setForceLogout(() => true);
    ipcRendererSend("close-second-window");
    setLoggingOut(() => true);
    await deleteActiveToken();
    await forceReload();
    clearLocalStorage();
    return logout();
  };

  const envValues = import.meta.env;

  const showPreferences = !isElectron || showLocalUserSettingsMenu;
  const secondaryText = { fontSize: "14px", color: theme.palette.neutral.main };

  return (
    <Box sx={{ height: "100%", overflowY: "auto" }} className="scrollable-content">
      <ModalForm open={loggingOut} allowClose={false}>
        <Box sx={{ display: "flex" }}>
          <Loading variant="white" size="large" baseSize={6} /> <Box sx={{ ml: 2 }}>Signing Out</Box>
        </Box>
      </ModalForm>
      {/*
        Two columns that wrap: the profile and the debug menu sit side by side when the content area fits both, and
        the debug menu drops below the profile when it does not. The flex bases decide where the wrap happens.
      */}
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 6, py: 4, px: 2 }}>
        <Stack sx={{ flex: "1 1 420px", maxWidth: "640px", minWidth: 0, gap: SECTION_GAP }}>
          <ProfileSection title="Account">
            {/* Name on the left, the workspace picker lined up on the right of the same row. */}
            <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 1.5 }}>
              <AvatarWithStatus accountId={myAccount?.id} />
              <Typography sx={{ fontWeight: 500, fontSize: "1.25rem", flex: 1, minWidth: 0 }}>
                {myAccount?.name}
              </Typography>
              <Box sx={{ flex: "none", maxWidth: "50%" }}>
                <WorkspaceDropdown />
              </Box>
            </Stack>
          </ProfileSection>

          {showPreferences && (
            <ProfileSection title="Preferences">
              {/* The Electron app already plays this sound from its main process, so the setting is browser-only. */}
              {!isElectron && (
                <Box>
                  {/* Names the radio group for screen readers, which would otherwise announce three bare options. */}
                  <Box id={NEW_MESSAGE_SOUND_HEADING_ID}>Play a sound for new driver messages</Box>
                  <Box sx={secondaryText}>
                    Only driver messages play a sound — messages from other dispatchers never do.
                  </Box>

                  <RadioGroup
                    aria-labelledby={NEW_MESSAGE_SOUND_HEADING_ID}
                    value={settings.newMessageSoundScope}
                    onChange={(event) => handleNewMessageSoundScopeChange(event.target.value as NewMessageSoundScope)}
                    sx={{ mt: 1 }}
                  >
                    {NEW_MESSAGE_SOUND_SCOPE_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.value}
                        value={option.value}
                        control={<Radio color="primary" />}
                        disableTypography
                        label={
                          <Box>
                            <Box>{option.label}</Box>
                            <Box sx={secondaryText}>{option.helperText}</Box>
                          </Box>
                        }
                        sx={{ alignItems: "start", mr: 0, "& .MuiRadio-root": { pt: 0 } }}
                      />
                    ))}
                  </RadioGroup>

                  {/* Both stay enabled while the ding is off, so a member can try the sounds before turning it on. */}
                  <Stack sx={{ mt: 2, gap: 2 }}>
                    <Box>
                      <Box id={NEW_MESSAGE_SOUND_ID_LABEL_ID}>Sound</Box>
                      <Select<NewMessageSoundId>
                        labelId={NEW_MESSAGE_SOUND_ID_LABEL_ID}
                        value={settings.newMessageSoundId}
                        onChange={(event) => handleNewMessageSoundIdChange(event.target.value)}
                        sx={{ mt: 1, width: 200 }}
                      >
                        {NEW_MESSAGE_SOUND_IDS.map((soundId) => (
                          <MenuItem key={soundId} value={soundId}>
                            {NEW_MESSAGE_SOUND_LABELS[soundId]}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                    <Box>
                      <Box id={NEW_MESSAGE_SOUND_VOLUME_LABEL_ID}>Volume</Box>
                      <Stack sx={{ flexDirection: "row", alignItems: "center", gap: 2, maxWidth: 320, pl: 1 }}>
                        <Slider
                          aria-labelledby={NEW_MESSAGE_SOUND_VOLUME_LABEL_ID}
                          min={MIN_NEW_MESSAGE_SOUND_VOLUME}
                          max={MAX_NEW_MESSAGE_SOUND_VOLUME}
                          step={NEW_MESSAGE_SOUND_VOLUME_STEP}
                          marks
                          value={settings.newMessageSoundVolume}
                          onChange={(_event, volume) => handleNewMessageSoundVolumeChange(volume)}
                          onChangeCommitted={(_event, volume) => handleNewMessageSoundVolumeCommit(volume)}
                          getAriaValueText={formatVolume}
                          sx={{ flex: 1 }}
                        />
                        <Box sx={{ ...secondaryText, minWidth: 40, textAlign: "right" }}>
                          {formatVolume(settings.newMessageSoundVolume)}
                        </Box>
                      </Stack>
                    </Box>
                  </Stack>
                </Box>
              )}

              {showLocalUserSettingsMenu && (
                <Box
                  component="button"
                  className="basic-button no-hover"
                  sx={{ display: "flex", textAlign: "left", alignItems: "start", gap: 2 }}
                  onClick={handleTeamDispatcherToggleClick}
                >
                  <Box>
                    <Switch checked={hideDriverMessages} />
                  </Box>
                  <Box>
                    <Box>Team Dispatching Mode</Box>
                    <Box sx={secondaryText}>
                      When ON, messages from drivers will not show as unread if another dispatcher sees them before me.
                    </Box>
                  </Box>
                </Box>
              )}
            </ProfileSection>
          )}

          <ProfileSection title="Support">
            <Stack sx={{ flexDirection: { xs: "column", sm: "row" }, gap: 1.5 }}>
              <Button variant="outlined" onClick={handleSubmitFeedback} sx={COMPACT_BUTTON}>
                Submit Feedback
              </Button>
              <Button variant="outlined" disabled={reloading} onClick={handleClearCacheAndReload} sx={COMPACT_BUTTON}>
                {reloading ? "Reloading..." : "Clear Cache & Reload"}
              </Button>
            </Stack>
          </ProfileSection>

          <Box sx={{ px: SECTION_PADDING_X }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<ExitToAppIcon />}
              onClick={handleLogout}
              sx={COMPACT_BUTTON}
            >
              Sign Out
            </Button>
          </Box>

          <Stack
            component="footer"
            sx={{
              px: SECTION_PADDING_X,
              flexDirection: "row",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 2,
              ...secondaryText,
            }}
          >
            {envValues?.VITE_NEAREST_WEB_RELEASE_TAG && <span>{envValues.VITE_NEAREST_WEB_RELEASE_TAG}</span>}
            <ElectronComponentLoader component={<ElectronAppVersion />} />
          </Stack>
        </Stack>

        {/* The debug menu is a separate tool, not a profile setting, so it gets its own column. */}
        {showDebugMenu && (
          <Box sx={{ flex: "1 1 560px", minWidth: 0 }}>
            <DebugFlagsPanel
              rows={debugFlagRows(flags, launchDarklyFlags, overriddenFlagKeys, debugFilter)}
              filter={debugFilter}
              onFilterChange={setDebugFilter}
              onChange={(key, value) => setFlags({ [key]: value })}
              onReset={clearFlagOverride}
              onResetAll={clearFlagOverrides}
            />
          </Box>
        )}
      </Box>
      <PortalDialog title="Leave Feedback" open={userFeedbackModalOpen} onClose={() => setUserFeedbackModalOpen(false)}>
        <UserFeedbackForm onClose={() => setUserFeedbackModalOpen(false)} />
      </PortalDialog>
      <PortalDialog
        title="Turn on Team Dispatching Mode?"
        open={teamDispatchConfirmOpen}
        onClose={handleTeamDispatchCancel}
      >
        <Stack sx={{ width: "100%", maxWidth: "500px", gap: 3 }}>
          <Typography sx={{ fontSize: "1rem" }}>
            Turning this on will resync your unreads. Unread indicators will be cleared where another dispatcher has
            already read the drivers message.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleTeamDispatchCancel}
              aria-label="Cancel turning on Team Dispatching Mode"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Cancel
            </Button>
            <Button
              disabled={clearingDb}
              variant="contained"
              color="primary"
              onClick={handleTeamDispatchConfirm}
              aria-label="Confirm turning on Team Dispatching Mode"
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Turn On
            </Button>
          </Box>
        </Stack>
      </PortalDialog>
    </Box>
  );
}
